import React, { useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { SVG_COMPONENTS } from '@/assets/admin';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useAdminColors } from '@/hooks/admin/useAdminColors';
import { useResidentTransactions } from '@/hooks/admin/useResidentTransactions';
import { useResponsive } from '@/hooks/common/useResponsive';
import { getApiErrorDescription } from '@/utils/admin/errorHandling';
import { formatDateShort } from '@/utils/admin/date';
import { Button } from '@/components/common/admin/mobile/Button';
import { TransactionSummary } from './TransactionSummary';
import { TransactionTable } from '../desktop/TransactionTable';
import { TransactionList } from '../mobile/TransactionList';
import { TransactionHistoryModal as DesktopTransactionHistoryModal } from '../desktop/TransactionHistoryModal';
import { TransactionHistoryModal as MobileTransactionHistoryModal } from '../mobile/TransactionHistoryModal';

interface TransactionHistorySectionProps {
  accountId?: number;
  leaseId: number;
  residentName?: string;
  /**
   * Visual variant.
   * - `default`: full section with header chrome, summary, expandable table.
   * - `compact-4`: most-recent-4 list (no header chrome — parent provides it).
   */
  variant?: 'default' | 'compact-4';
}

export const TransactionHistorySection: React.FC<TransactionHistorySectionProps> = ({
  accountId,
  leaseId,
  residentName,
  variant = 'default',
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const { showFullTable } = useResponsive();
  const styles = useStyles();
  const { admin, semantic } = useAdminColors();

  const { transactions, summary, isLoading, error } = useResidentTransactions({
    accountId: accountId || 0,
    leaseId,
    autoFetch: true,
  });

  const handleToggleExpand = () => {
    setIsExpanded(!isExpanded);
  };

  if (!accountId) {
    return null;
  }

  if (variant === 'compact-4') {
    if (isLoading) {
      return (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={admin.dataCell.textAccent} />
        </View>
      );
    }
    if (error) {
      return (
        <Text style={[styles.errorText, { color: semantic.status.error }]}>
          {getApiErrorDescription(error, 'Unable to load transactions.')}
        </Text>
      );
    }
    const last4 = (transactions ?? []).slice(0, 4);
    if (last4.length === 0) {
      return <Text style={styles.muted}>No recent transactions</Text>;
    }
    return (
      <View>
        {last4.map((t: any, idx: number) => {
          const amount = Number(t.amount ?? 0);
          const isPositive = amount > 0;
          const isLast = idx === last4.length - 1;
          return (
            <View
              key={t.transactionId ?? idx}
              style={[styles.kvRow, isLast && styles.kvRowLast]}
            >
              <Text style={styles.kvLeft} numberOfLines={1}>
                {t.transactionDate ? formatDateShort(new Date(t.transactionDate)) : '—'}
                {t.description ? ` · ${t.description}` : ''}
              </Text>
              <Text
                style={[
                  styles.amt,
                  { color: isPositive ? semantic.status.success : semantic.status.error },
                ]}
              >
                {isPositive ? '+' : '-'}${Math.abs(amount).toFixed(2)}
              </Text>
            </View>
          );
        })}
      </View>
    );
  }

  return (
    <View style={styles.section}>
      {/* Section Header */}
      <TouchableOpacity
        style={styles.header}
        onPress={handleToggleExpand}
        activeOpacity={0.7}
      >
        <Text style={styles.sectionTitle}>Transaction History</Text>
        <View style={styles.expandIcon}>
          {isExpanded ? (
            <SVG_COMPONENTS.CHEVRON_DOWN width={16} height={16} color={admin.dataCell.text} />
          ) : (
            <SVG_COMPONENTS.CHEVRON_RIGHT width={16} height={16} color={admin.dataCell.text} />
          )}
        </View>
      </TouchableOpacity>

      {/* Loading State */}
      {isLoading && (
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="small" color={admin.dataCell.textAccent} />
          <Text style={styles.loadingText}>Loading transactions...</Text>
        </View>
      )}

      {/* Error State */}
      {error && !isLoading && (
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: semantic.status.error }]}>
            {getApiErrorDescription(error, 'Unable to load transaction history. Please try again later.')}
          </Text>
        </View>
      )}

      {/* Summary */}
      {!isLoading && !error && (
        <TransactionSummary summary={summary} />
      )}

      {/* Expanded Content */}
      {isExpanded && !isLoading && !error && (
        <View style={styles.expandedContent}>
          {transactions.length === 0 ? (
            <View style={styles.emptyState}>
              <Text style={styles.emptyStateText}>
                No transactions found for this resident.
              </Text>
            </View>
          ) : (
            <>
              <View style={styles.transactionsContainer}>
                {showFullTable ? (
                  <TransactionTable transactions={transactions} maxRows={10} />
                ) : (
                  <TransactionList transactions={transactions} maxRows={5} />
                )}
              </View>

              {transactions.length > (showFullTable ? 10 : 5) && (
                <Button
                  title={`View All ${transactions.length} Transactions`}
                  onPress={() => setShowModal(true)}
                  variant="primary"
                  style={styles.viewAllButton}
                />
              )}
            </>
          )}
        </View>
      )}

      {/* Full Transaction History Modal */}
      {showFullTable ? (
        <DesktopTransactionHistoryModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          transactions={transactions}
          residentName={residentName}
        />
      ) : (
        <MobileTransactionHistoryModal
          visible={showModal}
          onClose={() => setShowModal(false)}
          transactions={transactions}
          residentName={residentName}
        />
      )}
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      section: {
        backgroundColor: 'transparent',
        paddingHorizontal: 16,
        paddingVertical: 20,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
      },
      header: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      },
      sectionTitle: {
        fontSize: 16,
        fontWeight: '600',
        color: colors.text.primary,
      },
      expandIcon: {
        justifyContent: 'center',
        alignItems: 'center',
      },
      loadingContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        paddingVertical: 20,
        gap: 8,
      },
      loadingText: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
      },
      errorContainer: {
        paddingVertical: 12,
      },
      errorText: {
        fontSize: 14,
        textAlign: 'center',
      },
      expandedContent: {
        marginTop: 8,
      },
      transactionsContainer: {
        maxHeight: 500,
        marginBottom: 12,
      },
      emptyState: {
        paddingVertical: 32,
        alignItems: 'center',
      },
      emptyStateText: {
        fontSize: 14,
        color: components.admin.dataCell.textMuted,
        textAlign: 'center',
      },
      viewAllButton: {
        marginTop: 8,
      },
      // compact-4 variant
      kvRow: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        paddingVertical: 8,
        borderBottomWidth: 1,
        borderBottomColor: components.admin.dataTable.border,
        gap: 12,
      },
      kvRowLast: {
        borderBottomWidth: 0,
      },
      kvLeft: {
        flex: 1,
        color: colors.text.primary,
        fontSize: 12.5,
      },
      amt: {
        fontSize: 12.5,
        fontWeight: '600',
      },
      muted: {
        color: colors.text.dim,
        fontSize: 12.5,
      },
    }),
  );
